import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText, Shield } from 'lucide-react';

export const ShoulderDislocation = () => {
  const faqs = [
    {
      question: 'What is the difference between a shoulder dislocation and a separated shoulder?',
      answer: 'A shoulder dislocation occurs when the humeral head (ball) comes completely out of the glenoid (socket) - a glenohumeral joint injury. A separated shoulder (AC joint separation) is an injury to the acromioclavicular joint where the clavicle meets the acromion. They are anatomically distinct injuries with different treatment approaches.'
    },
    {
      question: 'Will my shoulder dislocate again after the first time?',
      answer: 'Recurrence risk depends primarily on age at first dislocation. Studies show: Under 20 years: 70-90% recurrence risk. Age 20-30: 50-70% risk. Over 40: 10-20% risk. This is why age is a major factor in treatment decisions. A structured rehabilitation program focusing on rotator cuff and scapular strengthening significantly reduces recurrence risk.'
    },
    {
      question: 'Do I need surgery after my first shoulder dislocation?',
      answer: 'Not necessarily. Current evidence supports initial conservative treatment with structured rehabilitation for most first-time dislocations. Surgery is typically reserved for recurrent instability or specific high-risk situations. We focus on rotator cuff strengthening and scapular stabilization exercises, which have been shown to significantly reduce recurrence rates.'
    },
    {
      question: 'How long does recovery take after a shoulder dislocation?',
      answer: 'After conservative treatment: Initial healing 2-3 weeks, progressive rehabilitation 3-4 months, return to sport 4-6 months. After surgical stabilization: Sling 4-6 weeks, protected motion 6-12 weeks, strengthening 3-6 months, return to contact sport 6-9 months. Commitment to rehabilitation is crucial regardless of treatment path.'
    },
    {
      question: 'Can I play sports after a shoulder dislocation?',
      answer: 'Yes, most patients return to full sporting activity. The key is completing a proper rehabilitation program before returning. Contact sports and overhead activities require specific preparation. Your return timeline and need for surgery depend on your sport, position, and individual risk factors.'
    },
    {
      question: 'What is a Bankart lesion?',
      answer: 'A Bankart lesion is a tear of the anteroinferior labrum (cartilage rim) that typically occurs during anterior shoulder dislocation. It is present in 87-100% of first-time dislocations (Bankart, 1938). This labral tear is the primary cause of recurrent instability. Both bony and soft-tissue Bankart lesions exist, each requiring different surgical approaches if surgery becomes necessary.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="shoulder-dislocation-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Shoulder Dislocation Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Bankart repair & instability management by Dr. Deepthi Nandan Reddy at Apollo Hospitals
            </p>
          </div>
        </div>
      </section>

      {/* Understanding */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Understanding Shoulder Instability
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                The shoulder is the most mobile joint in the body, allowing the arm to move in virtually any direction. This mobility comes at a cost—the shoulder is inherently less stable than other joints, making it the most commonly dislocated major joint.
              </p>
              <p>
                <strong>Anatomy:</strong> The glenohumeral joint is a ball-and-socket joint where the humeral head (ball) articulates with the glenoid (socket). The glenoid is shallow, providing only 25% coverage of the humeral head. Stability comes from the labrum (cartilage rim), capsule, ligaments, and dynamic muscle control (rotator cuff and scapular muscles).
              </p>
              <p>
                <strong>Epidemiology:</strong> Shoulder dislocations account for 50% of all major joint dislocations. Incidence is 23.9 per 100,000 person-years, with peak occurrence in males aged 15-29 (sports/trauma) and females over 60 (falls).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Classification of Shoulder Instability
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-t-4 border-t-red-500">
                <CardHeader>
                  <CardTitle>Anterior (95%)</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  <p>Most common type. Humeral head displaces forward. Typically occurs with arm in abduction and external rotation.</p>
                  <p className="mt-2 text-sm">Associated: Bankart lesion, Hill-Sachs lesion</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-blue-500">
                <CardHeader>
                  <CardTitle>Posterior (2-4%)</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  <p>Rare, often missed. Humeral head displaces backward. Associated with seizures, electric shock, or direct trauma.</p>
                  <p className="mt-2 text-sm">Associated: Reverse Hill-Sachs lesion</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-purple-500">
                <CardHeader>
                  <CardTitle>Multidirectional</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  <p>Instability in multiple directions. Often due to generalized ligament laxity or repetitive microtrauma.</p>
                  <p className="mt-2 text-sm">Associated: Capsular redundancy</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Traumatic Dislocation (TUBS)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p><strong>T</strong>raumatic onset</p>
                  <p><strong>U</strong>nidirectional instability</p>
                  <p><strong>B</strong>ankart lesion typically present</p>
                  <p><strong>S</strong>urgery may be required for recurrence</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Atraumatic Instability (AMBRII)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p><strong>A</strong>traumatic onset</p>
                  <p><strong>M</strong>ultidirectional</p>
                  <p><strong>B</strong>ilateral (often)</p>
                  <p><strong>R</strong>ehabilitation primary treatment</p>
                  <p><strong>I</strong>nferior capsular shift if surgery needed</p>
                  <p><strong>I</strong>nterval closure</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Associated Injuries */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Associated Pathology
            </h2>
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Soft Tissue Injuries</p>
                      <p className="text-sm text-gray-600">• <strong>Bankart Lesion:</strong> Labral tear (87-100%)</p>
                      <p className="text-sm text-gray-600">• <strong>ALPSA Lesion:</strong> Anterior labral periosteal sleeve avulsion</p>
                      <p className="text-sm text-gray-600">• <strong>SLAP Tear:</strong> Superior labrum injury</p>
                      <p className="text-sm text-gray-600">• <strong>HAGL Lesion:</strong> Humeral avulsion of glenohumeral ligament</p>
                      <p className="text-sm text-gray-600">• <strong>Rotator Cuff Tear:</strong> Common in patients &gt;40 years</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Bony Injuries</p>
                      <p className="text-sm text-gray-600">• <strong>Hill-Sachs Lesion:</strong> Humeral head compression fracture (40-90%)</p>
                      <p className="text-sm text-gray-600">• <strong>Bony Bankart:</strong> Glenoid rim fracture (5-56%)</p>
                      <p className="text-sm text-gray-600">• <strong>Greater Tuberosity Fracture:</strong> More common in elderly</p>
                      <p className="text-sm text-gray-600">• <strong>Glenoid Track Concept:</strong> Engaging vs non-engaging Hill-Sachs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r">
                <p className="text-gray-700">
                  <strong>Critical Assessment:</strong> In patients over 40, rotator cuff tears occur in up to 35% of first-time dislocations and increase with age. MRI evaluation is essential in this age group before planning treatment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Clinical Presentation
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Acute Dislocation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-700">
                  <p>• Severe pain and muscle spasm</p>
                  <p>• Visible deformity (squared-off shoulder)</p>
                  <p>• Arm held in slight abduction and external rotation (anterior)</p>
                  <p>• Complete inability to move shoulder</p>
                  <p>• Possible numbness (axillary nerve injury in 5-35%)</p>
                  <p>• Palpable humeral head in abnormal position</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Recurrent Instability</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-700">
                  <p>• Episodes of subluxation or dislocation</p>
                  <p>• &ldquo;Dead arm&rdquo; sensation during activities</p>
                  <p>• Apprehension with arm in vulnerable positions</p>
                  <p>• Feeling of shoulder &ldquo;giving way&rdquo;</p>
                  <p>• Decreased confidence in shoulder</p>
                  <p>• Activity limitation and avoidance</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Diagnostic Evaluation
              </h2>
            </div>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Clinical Examination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><strong>Instability Tests:</strong></p>
                  <p className="text-sm text-gray-600">• <strong>Apprehension Test:</strong> Positive when patient feels impending dislocation with arm in 90° abduction and external rotation</p>
                  <p className="text-sm text-gray-600">• <strong>Relocation Test:</strong> Relief of apprehension with posterior pressure on humeral head</p>
                  <p className="text-sm text-gray-600">• <strong>Anterior/Posterior Drawer:</strong> Assesses translation of humeral head</p>
                  <p className="text-sm text-gray-600">• <strong>Sulcus Sign:</strong> Inferior laxity assessment for MDI</p>
                  <p className="text-sm text-gray-600">• <strong>Load and Shift:</strong> Quantifies glenohumeral translation</p>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">X-rays</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600">
                    <p>• AP, Lateral (Y-view), Axillary</p>
                    <p>• Confirm dislocation direction</p>
                    <p>• Identify fractures</p>
                    <p>• West Point view for bony Bankart</p>
                    <p>• Stryker notch for Hill-Sachs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">MRI / MR Arthrogram</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600">
                    <p>• Gold standard for soft tissue</p>
                    <p>• Labral tears (Bankart, SLAP)</p>
                    <p>• Capsular pathology</p>
                    <p>• Rotator cuff integrity</p>
                    <p>• Cartilage assessment</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">CT Scan</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600">
                    <p>• Bone loss quantification</p>
                    <p>• Glenoid track assessment</p>
                    <p>• 3D reconstruction planning</p>
                    <p>• Essential if surgery planned</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Evidence-Based Treatment */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Evidence-Based Treatment
              </h2>
            </div>
            
            {/* Evidence Note */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r">
              <div className="flex items-start gap-2">
                <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Treatment recommendations based on systematic reviews, Cochrane database, and guidelines from ISAKOS, ASES, and published long-term outcomes studies.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Emergency Treatment */}
              <Card className="border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">EMERGENCY</span>
                    Acute Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Closed Reduction:</strong> Prompt reduction under sedation/analgesia using gentle techniques (Stimson, Cunningham, external rotation method). Neurovascular assessment before and after reduction is mandatory.</p>
                  <p><strong>Post-Reduction:</strong> Confirm reduction with X-rays, immobilize in sling, ice, and analgesia.</p>
                </CardContent>
              </Card>

              {/* First-Time Dislocation - IMPORTANT */}
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">FIRST-TIME DISLOCATION</span>
                    Conservative Treatment Protocol
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <div className="bg-green-50 p-4 rounded-lg mb-4">
                    <p className="font-semibold text-green-800 mb-2">Our Approach for First-Time Dislocations:</p>
                    <p>We recommend <strong>structured rehabilitation</strong> as first-line treatment for most first-time shoulder dislocations. This evidence-based approach focuses on:</p>
                  </div>
                  
                  <p><strong>Phase 1 - Protection (Weeks 0-3):</strong></p>
                  <p>• Sling immobilization for comfort (2-3 weeks)</p>
                  <p>• Pain and inflammation control</p>
                  <p>• Gentle pendulum exercises when pain allows</p>
                  
                  <p className="mt-3"><strong>Phase 2 - Early Rehabilitation (Weeks 3-8):</strong></p>
                  <p>• Progressive range of motion exercises</p>
                  <p>• <strong>Rotator cuff strengthening</strong> - Internal/external rotation exercises</p>
                  <p>• <strong>Scapular stabilization (Scapular Setting)</strong> - Critical for dynamic stability</p>
                  <p>• Core stability integration</p>
                  
                  <p className="mt-3"><strong>Phase 3 - Advanced Strengthening (Weeks 8-16):</strong></p>
                  <p>• Progressive resistance training</p>
                  <p>• Proprioceptive training</p>
                  <p>• Sport-specific conditioning</p>
                  <p>• Gradual return to activity</p>

                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Evidence:</strong> Studies show that a comprehensive rehabilitation program focusing on rotator cuff and scapular muscle strengthening can reduce recurrence rates by 20-40%. The key is completing the full program before returning to high-risk activities.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Surgical Treatment */}
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">RECURRENT INSTABILITY</span>
                    Surgical Stabilization
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Indications for Surgery:</strong></p>
                  <p>• Recurrent dislocations (2 or more episodes) despite rehabilitation</p>
                  <p>• Significant bone loss (&gt;20% glenoid or engaging Hill-Sachs)</p>
                  <p>• Failed conservative treatment with ongoing instability</p>
                  <p>• Associated repairable pathology (rotator cuff tear, bony fracture)</p>
                  
                  <div className="mt-4 space-y-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Arthroscopic Bankart Repair</p>
                      <p>• Gold standard for soft-tissue instability without significant bone loss</p>
                      <p>• Suture anchor repair of labrum to glenoid rim</p>
                      <p>• Success rates: 85-95% in appropriately selected patients</p>
                      <p>• Advantages: Minimally invasive, faster recovery</p>
                    </div>
                    
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Latarjet Procedure (Bone Block)</p>
                      <p>• For significant glenoid bone loss (&gt;20-25%)</p>
                      <p>• Coracoid transfer to anterior glenoid</p>
                      <p>• Provides bone augmentation + sling effect</p>
                      <p>• Lowest recurrence rates (2-5%)</p>
                    </div>
                    
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Remplissage Procedure</p>
                      <p>• For engaging Hill-Sachs lesions</p>
                      <p>• Infraspinatus tenodesis into humeral head defect</p>
                      <p>• Often combined with Bankart repair</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Recovery & Return to Activity
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-700">Conservative Treatment</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Sling: 2-3 weeks</p>
                  <p>• Range of motion: 3-6 weeks</p>
                  <p>• Strengthening: 6-12 weeks</p>
                  <p>• Non-contact sports: 3 months</p>
                  <p>• Contact sports: 4-6 months</p>
                  <p className="text-sm text-gray-500 mt-3">Success depends on commitment to rehabilitation program</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-orange-700">Post-Surgical Recovery</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Sling: 4-6 weeks</p>
                  <p>• Passive/active-assisted ROM: 6-10 weeks</p>
                  <p>• Strengthening: 10-16 weeks</p>
                  <p>• Non-contact sports: 4-5 months</p>
                  <p>• Contact sports: 6-9 months</p>
                  <p className="text-sm text-gray-500 mt-3">Stabilization success rate: 90-95%</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
              <p className="text-gray-700">
                <strong>Key to Success:</strong> Regardless of treatment path, completing the full rehabilitation program is essential. Premature return to activity before adequate strength and control is regained significantly increases re-injury risk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific References */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References & Guidelines</h3>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-sm text-gray-600 space-y-2">
              <p>1. Bankart ASB. The pathology and treatment of recurrent dislocation of the shoulder joint. Br J Surg. 1938</p>
              <p>2. Cochrane Review: Surgical versus conservative treatment for acute anterior shoulder dislocation</p>
              <p>3. ISAKOS Scientific Committee: Consensus Statement on Shoulder Instability</p>
              <p>4. Burkhart SS, De Beer JF. Traumatic glenohumeral bone defects and their relationship to failure of arthroscopic Bankart repairs. Arthroscopy. 2000</p>
              <p>5. Di Giacomo G, et al. The Glenoid Track Concept. AJSM. 2014</p>
              <p>6. Olds M, et al. Risk factors for recurrence after first traumatic anterior shoulder dislocation: a systematic review. Br J Sports Med. 2015</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">
                Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection />
    </div>
  );
};
